/** @format */

import React, { useEffect, useState, useCallback } from "react";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Loader from "../../elements/Loader";
import { toast } from "react-toastify";
import ApiService from "../../services/apiService";
import Select from "react-select";
import { useAuth } from "../../hooks/useAuth";

const validationSchema = Yup.object().shape({
  serviceId: Yup.string().required("Puja name is required"),
  userId: Yup.string().required("Customer is required"),
  pujaDateTime: Yup.string().required("Puja date & time is required"),
  bookingStatus: Yup.string().required("Booking status is required"),
  paymentStatus: Yup.string().required("Payment status is required"),
  actualPrice: Yup.number()
    .typeError("Price must be a number")
    .required("Price is required")
    .min(0, "Price cannot be negative"),
  createdAt: Yup.string().required("Creation date & time is required"),
});

export default function AddPastBooking() {
  const navigate = useNavigate();
  const { userId } = useAuth();

  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pandits, setPandits] = useState([]);
  const [variants, setVariants] = useState([]);
  const [userAddresses, setUserAddresses] = useState([]);

  // Controlled selects (react-select)
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPandit, setSelectedPandit] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // ─── Data fetchers ───────────────────────────────────────────────────────────

  const getPujaList = useCallback(async () => {
    try {
      const data = await ApiService.get("service/list");
      if (data?.data) {
        setServices(
          data.data.map((item) => ({
            value: item.id,
            label: item.title,
            price: item.price,
          }))
        );
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const getCustomers = useCallback(async (searchTerm = "") => {
    try {
      const data = await ApiService.get(
        `user/role/user?dropdown=true${searchTerm ? `&search=${searchTerm}` : ""}`
      );
      if (data?.data) {
        setCustomers(
          data.data.map((item) => ({
            value: item.id,
            label: `${item.name}, ${item.phone}`,
          }))
        );
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const getPandits = useCallback(async (searchTerm = "") => {
    try {
      const data = await ApiService.get(
        `user/role/pandit?dropdown=true${searchTerm ? `&search=${searchTerm}` : ""}`
      );
      if (data?.data) {
        setPandits(
          data.data.map((item) => ({
            value: item.id,
            label: `${item.name}, ${item.phone}`,
          }))
        );
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const getVariantsByServiceId = useCallback(async (serviceId) => {
    try {
      const data = await ApiService.get(`service/${serviceId}/admin`);
      const basicOption = {
        value: data?.result?.id || serviceId,
        label: data?.result?.variantType || 'Basic',
        price: data?.result?.price,
        isBasic: true,
      };
      if (data?.result?.variants) {
        const variantOptions = data.result.variants
          .filter((item) => !item.isCustomPuja)
          .map((item) => ({
            value: item.id,
            label: item.variantType || "Unknown Variant",
            price: item.price,
          }));
        setVariants([basicOption, ...variantOptions]);
      } else {
        setVariants([basicOption]);
      }
    } catch (err) {
      console.error(err);
      setVariants([]);
    }
  }, []);

  const getUserAddresses = useCallback(async (customUserId) => {
    if (!customUserId) return;
    try {
      const response = await ApiService.get(`user/address/${customUserId}`);
      const addresses = response?.data || [];
      setUserAddresses(
        addresses.map((addr) => ({
          value: addr.id,
          label: [addr.fullAddress, addr.locality, addr.direction]
            .filter(Boolean)
            .join(", "),
        }))
      );
    } catch (error) {
      console.error("Error fetching user addresses:", error);
      setUserAddresses([]);
    }
  }, []);

  useEffect(() => {
    getPujaList();
    getCustomers();
    getPandits();
  }, [getPujaList, getCustomers, getPandits]);

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const onSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const payload = {
        serviceId: values.serviceId,
        userId: values.userId,
        panditId: values.panditId || null,
        variantId: values.variantId || null,
        addressId: values.addressId || null,
        pujaDateTime: values.pujaDateTime,
        bookingStatus: values.bookingStatus,
        paymentStatus: values.paymentStatus,
        actualPrice: parseFloat(values.actualPrice),
        finalPrice: values.discountAmount
          ? parseFloat(values.actualPrice) - parseFloat(values.discountAmount)
          : parseFloat(values.actualPrice),
        discountAmount: values.discountAmount
          ? parseFloat(values.discountAmount)
          : null,
        paymentMethod: values.paymentMethod || null,
        paymentId: values.paymentId || null,
        transcribeText: values.transcribeText || null,
        beId: userId,
        adminNote: values.adminNote || null,
        createdAt: values.createdAt || null,
      };

      const response = await ApiService.post("booking/past-booking", payload);
      if (response?.msg === "success") {
        toast.success("Past booking created successfully — no notifications sent.");
        navigate("/booking");
      } else {
        toast.error(response?.error || "Failed to create past booking");
      }
    } catch (err) {
      console.error("Error creating past booking:", err);
      toast.error(
        err?.response?.data?.error || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="row">
      {loading && <Loader />}
      <div className="col-xl-10 col-lg-12 offset-xl-1">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title mb-0">Add Past Booking</h4>
          </div>
          <div className="card-body">
            <Formik
              initialValues={{
                serviceId: "",
                userId: "",
                panditId: "",
                variantId: "",
                addressId: "",
                pujaDateTime: "",
                bookingStatus: "puja_completed",
                paymentStatus: "done",
                actualPrice: "",
                discountAmount: "",
                paymentMethod: "cash",
                paymentId: "",
                transcribeText: "",
                adminNote: "",
                createdAt: new Date().toISOString().slice(0, 16),
              }}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({ setFieldValue, values, isSubmitting }) => (
                <Form>
                  <div className="row">

                    {/* ── Section: Booking Info ── */}
                    <div className="col-12 mb-3">
                      <h6 className="text-muted border-bottom pb-2">
                        Booking Information
                      </h6>
                    </div>

                    {/* Customer */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Customer <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={customers}
                          value={selectedCustomer}
                          onChange={(opt) => {
                            setSelectedCustomer(opt);
                            setFieldValue("userId", opt ? opt.value : "");
                            // Reset address when customer changes
                            setSelectedAddress(null);
                            setFieldValue("addressId", "");
                            if (opt) {
                              getUserAddresses(opt.value);
                            } else {
                              setUserAddresses([]);
                            }
                          }}
                          onInputChange={(val) => {
                            clearTimeout(window.customerSearchTimeout);
                            window.customerSearchTimeout = setTimeout(
                              () => getCustomers(val),
                              300
                            );
                          }}
                          isClearable
                          placeholder="Search customer by name or phone..."
                          classNamePrefix="react-select"
                        />
                        <ErrorMessage
                          name="userId"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </div>
                    </div>

                    {/* Puja / Service */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Puja Name <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={services}
                          value={selectedService}
                          onChange={(opt) => {
                            setSelectedService(opt);
                            setFieldValue("serviceId", opt ? opt.value : "");
                            // Reset variant when puja changes
                            setSelectedVariant(null);
                            setFieldValue("variantId", "");
                            setVariants([]);
                            if (opt) {
                              getVariantsByServiceId(opt.value);
                              // Pre-fill price from service
                              if (opt.price && !values.actualPrice) {
                                setFieldValue("actualPrice", opt.price);
                              }
                            }
                          }}
                          isClearable
                          placeholder="Select puja..."
                          classNamePrefix="react-select"
                        />
                        <ErrorMessage
                          name="serviceId"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </div>
                    </div>

                    {/* Variant */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Puja Variant</label>
                        <Select
                          options={variants}
                          value={selectedVariant}
                          onChange={(opt) => {
                            setSelectedVariant(opt);
                            setFieldValue("variantId", opt ? opt.value : "");
                            // Update price when variant selected
                            if (opt?.price) {
                              setFieldValue("actualPrice", opt.price);
                            }
                          }}
                          isClearable
                          placeholder={
                            variants.length === 0
                              ? "Select puja first..."
                              : "Select variant..."
                          }
                          isDisabled={variants.length === 0}
                          classNamePrefix="react-select"
                        />
                      </div>
                    </div>

                    {/* Acharya / Pandit */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Assigned Acharya</label>
                        <Select
                          options={pandits}
                          value={selectedPandit}
                          onChange={(opt) => {
                            setSelectedPandit(opt);
                            setFieldValue("panditId", opt ? opt.value : "");
                          }}
                          onInputChange={(val) => {
                            clearTimeout(window.panditSearchTimeout);
                            window.panditSearchTimeout = setTimeout(
                              () => getPandits(val),
                              300
                            );
                          }}
                          isClearable
                          placeholder="Search acharya by name or phone..."
                          classNamePrefix="react-select"
                        />
                      </div>
                    </div>

                    {/* User Address */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Service Address</label>
                        <Select
                          options={userAddresses}
                          value={selectedAddress}
                          onChange={(opt) => {
                            setSelectedAddress(opt);
                            setFieldValue("addressId", opt ? opt.value : "");
                          }}
                          isClearable
                          placeholder={
                            !values.userId
                              ? "Select customer first..."
                              : userAddresses.length === 0
                              ? "No addresses found for this customer"
                              : "Select address..."
                          }
                          isDisabled={!values.userId}
                          classNamePrefix="react-select"
                        />
                      </div>
                    </div>

                    {/* Puja Date & Time */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Puja Date & Time{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="datetime-local"
                          name="pujaDateTime"
                          className="form-control"
                        />
                        <ErrorMessage
                          name="pujaDateTime"
                          component="div"
                          className="text-danger small mt-1"
                        />
                        <small className="text-muted">
                          Past dates are allowed for backdated bookings.
                        </small>
                      </div>
                    </div>

                    {/* Created At */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Created At <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="datetime-local"
                          name="createdAt"
                          className="form-control"
                        />
                        <ErrorMessage
                          name="createdAt"
                          component="div"
                          className="text-danger small mt-1"
                        />
                        <small className="text-muted">
                          The original date the booking was "created".
                        </small>
                      </div>
                    </div>



                    {/* ── Section: Status ── */}
                    <div className="col-12 mb-3 mt-2">
                      <h6 className="text-muted border-bottom pb-2">
                        Status
                      </h6>
                    </div>

                    {/* Booking Status */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Booking Status <span className="text-danger">*</span>
                        </label>
                        <Field
                          as="select"
                          name="bookingStatus"
                          className="form-control"
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="acharya_assigned">
                            Acharya Assigned
                          </option>
                          <option value="samagri_delivered">
                            Samagri Delivered
                          </option>
                          <option value="puja_started">Puja Started</option>
                          <option value="puja_completed">Puja Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </Field>
                        <ErrorMessage
                          name="bookingStatus"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </div>
                    </div>

                    {/* Payment Status */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Payment Status <span className="text-danger">*</span>
                        </label>
                        <Field
                          as="select"
                          name="paymentStatus"
                          className="form-control"
                        >
                          <option value="pending">Pending</option>
                          <option value="partial">Partial</option>
                          <option value="done">Done (Paid)</option>
                        </Field>
                        <ErrorMessage
                          name="paymentStatus"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </div>
                    </div>

                    {/* ── Section: Pricing ── */}
                    <div className="col-12 mb-3 mt-2">
                      <h6 className="text-muted border-bottom pb-2">
                        Pricing
                      </h6>
                    </div>

                    {/* Actual Price */}
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label">
                          Price (₹) <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="number"
                          name="actualPrice"
                          className="form-control"
                          placeholder="e.g. 5100"
                          min="0"
                        />
                        <ErrorMessage
                          name="actualPrice"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </div>
                    </div>

                    {/* Discount Amount */}
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label">
                          Discount Amount (₹)
                        </label>
                        <Field
                          type="number"
                          name="discountAmount"
                          className="form-control"
                          placeholder="e.g. 500 (optional)"
                          min="0"
                        />
                        <ErrorMessage
                          name="discountAmount"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </div>
                    </div>

                    {/* Final Price Preview */}
                    <div className="col-lg-4">
                      <div className="mb-3">
                        <label className="form-label">Final Price (₹)</label>
                        <div className="form-control bg-light text-success fw-bold">
                          ₹
                          {values.actualPrice
                            ? (
                                parseFloat(values.actualPrice || 0) -
                                parseFloat(values.discountAmount || 0)
                              ).toFixed(2)
                            : "—"}
                        </div>
                        <small className="text-muted">
                          Auto-calculated (Price − Discount)
                        </small>
                      </div>
                    </div>

                    {/* ── Section: Payment Record ── */}
                    <div className="col-12 mb-3 mt-2">
                      <h6 className="text-muted border-bottom pb-2">
                        Payment Record
                      </h6>
                    </div>

                    {/* Payment Method */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Payment Method</label>
                        <Field
                          as="select"
                          name="paymentMethod"
                          className="form-control"
                        >
                          <option value="">-- Not recorded --</option>
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="online">Online (Razorpay)</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="cheque">Cheque</option>
                        </Field>
                      </div>
                    </div>

                    {/* Payment ID / Reference */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Payment ID / Reference
                        </label>
                        <Field
                          type="text"
                          name="paymentId"
                          className="form-control"
                          placeholder="e.g. UPI ref, Razorpay ID (optional)"
                        />
                      </div>
                    </div>

                    {/* ── Section: Notes ── */}
                    <div className="col-12 mb-3 mt-2">
                      <h6 className="text-muted border-bottom pb-2">
                        Notes
                      </h6>
                    </div>

                    {/* Transcribe / Call Notes */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Call Notes</label>
                        <Field
                          as="textarea"
                          name="transcribeText"
                          className="form-control"
                          rows="3"
                          placeholder="Any call or conversation notes..."
                        />
                      </div>
                    </div>

                    {/* Admin Note */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Admin Note (Internal)
                        </label>
                        <Field
                          as="textarea"
                          name="adminNote"
                          className="form-control"
                          rows="3"
                          placeholder="Why is this being added as a past booking? (for internal reference)"
                        />
                      </div>
                    </div>



                    {/* ── Actions ── */}
                    <div className="col-12 mt-2">
                      <div className="d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isSubmitting || loading}
                        >
                          {isSubmitting || loading
                            ? "Saving..."
                            : "Add Past Booking"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => navigate("/booking")}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
}
